<template>
  <!-- Modern Horizontal Gift Card -->
  <div
    class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-md"
    :class="{
      'ring-2 ring-blue-400 border-blue-400': isAssignedAndNotPurchased,
      'ring-2 ring-green-400 border-green-400 opacity-75': isAssignedAndPurchased,
    }"
>
    <div class="flex items-start gap-3 sm:gap-4">
      <!-- Image Section -->
      <div class="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
        <a v-if="gift.url" :href="safeUrl" target="_blank" rel="noopener noreferrer" class="block w-full h-full">
          <img :src="getImageURL(gift.id)" class="w-full h-full object-cover" />
        </a>
        <img v-else :src="getImageURL(gift.id)" class="w-full h-full object-cover" />

        <!-- Price Badge -->
        <div v-if="gift.price" class="absolute top-2 right-2">
          <span
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 shadow-sm"
>
            ${{ gift.price }}
          </span>
        </div>
      </div>

      <!-- Content Section -->
      <div class="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0 space-y-3">
        <!-- Header with title and domain -->
        <div class="flex-1">
          <div class="flex items-start justify-between mb-1">
            <h3 class="font-semibold text-gray-900 line-clamp-2 text-sm sm:text-base pr-2">
              {{ gift.name }}
            </h3>
            <div v-if="gift.url" class="flex-shrink-0">
              <span class="hidden md:inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-600">
                {{ getDomainName(gift.url) }}
              </span>
            </div>
          </div>

          <!-- Description (if available) -->
          <p v-if="gift.description" class="text-xs sm:text-sm text-gray-600 line-clamp-2">
            {{ gift.description }}
          </p>
        </div>

        <!-- Assignment Meta -->
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
          <div v-if="assignedAt" class="flex items-center space-x-1">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
/>
            </svg>
            <span>Saved {{ formatDateTime(assignedAt) }}</span>
          </div>
          <div v-if="purchasedAt" class="flex items-center space-x-1 text-green-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="m5 13 4 4L19 7"
/>
            </svg>
            <span>Purchased {{ formatDateTime(purchasedAt) }}</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap sm:flex-nowrap items-center sm:justify-end gap-2 sm:gap-3">
          <!-- Save/Saved Button -->
          <button
@click="toggleAssignment"
            class="flex items-center justify-center sm:justify-start space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors min-w-0 sm:min-w-[120px] w-full sm:w-auto"
            :class="isAssigned ?
              'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100' :
              'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'"
>
            <input
@change="UpdateFriendLink($event, friend.id, friend.GiftLink.items)" type="checkbox"
              :id="'assign' + friend.id + gift.id" :value="gift.id" :checked="isAssigned"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
/>
            <span class="font-medium">{{ isAssigned ? 'Saved' : 'Save' }}</span>
          </button>

          <!-- Purchased Button -->
          <button
@click="togglePurchased" :disabled="!isAssigned"
            class="flex items-center justify-center sm:justify-start space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors min-w-0 sm:min-w-[120px] w-full sm:w-auto"
            :class="isPurchased ?
              'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100' :
              isAssigned ? 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100' :
                'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'"
>
            <input
@change="UpdatePurchaseStatus($event, gift.id, friend.GiftLink.items)" type="checkbox"
              :id="'purchased' + friend.id + gift.id" :value="gift.id" :checked="isPurchased" :disabled="!isAssigned"
              class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
/>
            <span class="font-medium">Purchased</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from "aws-amplify";

import {
  createGiftFriend,
  deleteGiftFriend,
  updateGiftFriend,
} from "../graphql/customMutations";

export default {
  name: "FriendAssignItem",
  props: {
    friend: {
      type: Object,
      default() {
        return {};
      },
    },
    gift: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      giftImages: {},
      isPurchased: false,
      isAssigned: false,
      tempGiftLinkID: "",
      assignedTimestamp: null,
      purchasedTimestamp: null,
    };
  },
  computed: {
    isAssignedAndNotPurchased() {
      return this.isAssigned && !this.isPurchased;
    },
    isAssignedAndPurchased() {
      return this.isAssigned && this.isPurchased;
    },
    safeUrl() {
      if (!this.gift.url) return '#';
      const url = this.gift.url.trim();
      return url.startsWith('http://') || url.startsWith('https://') ? url : '#';
    },
    giftLink() {
      return this.friend?.GiftLink?.items?.find((value) => value.gift?.id === this.gift.id) || null;
    },
    assignedAt() {
      return this.assignedTimestamp || this.giftLink?.createdAt || null;
    },
    purchasedAt() {
      if (this.purchasedTimestamp) {
        return this.purchasedTimestamp;
      }
      if (this.giftLink?.purchased) {
        return this.giftLink.updatedAt || this.giftLink.createdAt || null;
      }
      return null;
    },
  },
  created() {
    this.getImage(this.gift);

    this.refreshGiftLinkState();
  },
  watch: {
    friend: {
      handler() {
        this.refreshGiftLinkState();
      },
      deep: true,
    },
  },
  methods: {
    toggleAssignment() {
      // This method provides visual feedback but the actual logic is in the checkbox change handler
    },
    togglePurchased() {
      // This method provides visual feedback but the actual logic is in the checkbox change handler
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    getDomainName(url) {
      try {
        const urlObj = new URL(url);
        let domain = urlObj.hostname;

        if (domain.startsWith("www.")) {
          domain = domain.slice(4);
        }

        return domain;
      } catch {
        return '';
      }
    },
    getImageURL(giftId) {
      return this.giftImages[giftId];
    },
    getImage(gift) {
      const giftId = gift.id;
      const image = gift.image;
      if (!image) {
        this.giftImages[giftId] = require(`@/assets/gift.png`);
        return;
      }
      if (image?.includes("https://")) {
        this.giftImages[giftId] = image;
      } else {
        this.giftImages[giftId] =
          "https://" + process.env.VUE_APP_GIFT_IMAGE_BUCKET + "/public/" + image;
      }
    },
    refreshGiftLinkState() {
      const link = this.giftLink;
      this.isAssigned = !!link;
      this.assignedTimestamp = link?.createdAt || null;
      this.isPurchased = !!(link && link.purchased);
      this.purchasedTimestamp = link?.purchased ? (link.updatedAt || link.createdAt || null) : null;
      this.tempGiftLinkID = link?.id || "";
    },
    async UpdateFriendLink(e, friendID, map) {
      if (e.target.checked) {
        try {
          const giftFriend = { giftId: e.target.value, friendId: friendID };
          const response = await API.graphql({
            query: createGiftFriend,
            variables: { input: giftFriend },
          });
          this.isAssigned = true;
          this.tempGiftLinkID = response.data.createGiftFriend.id;
          this.assignedTimestamp =
            response?.data?.createGiftFriend?.createdAt || new Date().toISOString();
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          var giftLinkId = "";
          map.forEach((value) => {
            if (value.gift.id === e.target.value) {
              giftLinkId = value.id;
            }
          });
          const mutationInput = { id: giftLinkId };
          const response = await API.graphql({
            query: deleteGiftFriend,
            variables: { input: mutationInput },
          });
        } catch (error) {
          console.log(error);
        }
        this.isAssigned = false;
        this.isPurchased = false;
        this.assignedTimestamp = null;
        this.purchasedTimestamp = null;
        this.tempGiftLinkID = "";
      }
    },
    async UpdatePurchaseStatus(e, giftID, map) {
      var giftLinkId = "";
      if (this.tempGiftLinkID == "") {
        map.forEach((value) => {
          if (value.gift.id === giftID) {
            giftLinkId = value.id;
          }
        });
      } else {
        giftLinkId = this.tempGiftLinkID;
      }

      if (e.target.checked) {
        try {
          const purchaseStatus = { purchased: true, id: giftLinkId };
          const response = await API.graphql({
            query: updateGiftFriend,
            variables: { input: purchaseStatus },
          });
          this.isPurchased = true;
          this.purchasedTimestamp =
            response?.data?.updateGiftFriend?.updatedAt || new Date().toISOString();
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          const purchaseStatus = { purchased: false, id: giftLinkId };
          const response = await API.graphql({
            query: updateGiftFriend,
            variables: { input: purchaseStatus },
          });
          this.isPurchased = false;
          this.purchasedTimestamp = null;
        } catch (error) {
          console.log(error);
        }
      }
    },
  },
};
</script>
